import { Box } from "@chakra-ui/react";
import { FC, ReactNode } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

import { getAppMode } from "utils/getAppMode";

const recaptchaSiteKey = process.env.RECAPTCHA_V3_SITE_KEY;

// reCAPTCHA renders its widget (the badge) into this element, so we can hide it
// locally instead of overriding `.grecaptcha-badge` with global CSS. Without an
// explicit container the library auto-renders the badge into <body>.
const RECAPTCHA_CONTAINER_ID = "recaptcha-badge-container";

/**
 * Loads the reCAPTCHA v3 script once for the whole tree so any LeadForm can
 * request a token. Active only in the live environment; in dev/prelive it
 * renders children untouched so the app keeps working without a captcha.
 */
export const RecaptchaProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { isLiveMode } = getAppMode();

    if (!isLiveMode || !recaptchaSiteKey) {
        return <>{children}</>;
    }

    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={recaptchaSiteKey}
            scriptProps={{ async: true, defer: true, appendTo: "head" }}
            container={{
                element: RECAPTCHA_CONTAINER_ID,
                parameters: { badge: "inline" },
            }}
        >
            <Box
                id={RECAPTCHA_CONTAINER_ID}
                // Hidden, not removed: the badge hosts the reCAPTCHA iframe
                // that `execute` relies on, and `display: none` can break it.
                // `fixed` keeps the hidden inline badge (256x60) out of the
                // layout flow, since `visibility: hidden` still reserves space.
                position="fixed"
                visibility="hidden"
            />
            {children}
        </GoogleReCaptchaProvider>
    );
};
